<template>
  <div id="adaptive-flashcard-wrapper">
    <calculator-modal
      v-if="showCalculator"
      @close="showCalculator = false"
    ></calculator-modal>
    <page-layout>
      <template #header>
        <simulation-header
          :title="simulation?.name || ''"
          :subtitle="userName"
          :time-left-in-seconds="timeLeftInSeconds"
          :question-number="session?.position || null"
          :include-calculator="true"
          @calculator-clicked="showCalculator = !showCalculator"
        ></simulation-header>
      </template>
      <div
        v-if="flashcard"
        :key="flashcard.id"
        class="flashcard-content two-column"
        data-test="adaptive-flashcard-content"
      >
        <div class="first-column">
          <scenario-box :flashcard-id="flashcard.id"></scenario-box>
          <asset-tray :flashcard="flashcard"></asset-tray>
        </div>
        <flashcard-body
          :key="`${flashcard.id}-${fillCount}`"
          :flashcard="flashcard"
          :flashcard-state="answerState"
          @answers-selected="answersSelected"
          @set-raw-answer-data="setRawAnswerData"
        ></flashcard-body>
      </div>
      <template #footer>
        <page-footer
          button-text="Next"
          @next-button-clicked="submitAnswer"
        ></page-footer>
      </template>
    </page-layout>
    <response-required-modal
      v-if="showResponseRequired"
      @close="showResponseRequired = false"
    />
    <adaptive-debug-panel
      v-if="debug"
      :debug="debug"
      :allow-fill="true"
      @fill-correct="fillCorrect"
    />
  </div>
</template>

<script>
/**
 * Question page for adaptive (CAT) simulations: one question at a time, chosen by the server.
 * There is no going back, no bookmarks and no review; each answer is sent once, on Next.
 */
import _ from 'lodash';
import PageLayout from '@components/sims/single-section-sim/PageLayout.vue';
import PageFooter from '@components/sims/single-section-sim/PageFooter.vue';
import SimulationHeader from '@components/sims/single-section-sim/SimulationHeader.vue';
import FlashcardBody from '@components/sims/single-section-sim/FlashcardBody.vue';
import AssetTray from '@components/sims/single-section-sim/AssetTray.vue';
import ScenarioBox from '@components/sims/single-section-sim/ScenarioBox.vue';
import ResponseRequiredModal from '@components/sims/single-section-sim/ResponseRequiredModal.vue';
import CalculatorModal from '@/components/calculator/CalculatorModal.vue';
import AdaptiveDebugPanel from '@/components/sims/adaptive/AdaptiveDebugPanel.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import ScenarioBoxMixin from '@/mixins/scenario-box';
import GroupingsMixin from '@/mixins/groupings';

const flashcardTypesWithRawAnswerData = ['Numeric Entry', 'Ordered Response', 'Matrix Grid', 'Hotspot'];

export default {
  name: 'AdaptiveFlashcard',
  components: {
    AdaptiveDebugPanel,
    AssetTray,
    CalculatorModal,
    FlashcardBody,
    PageFooter,
    PageLayout,
    ResponseRequiredModal,
    ScenarioBox,
    SimulationHeader,
  },
  mixins: [WuxSimulationsMixin, ScenarioBoxMixin, GroupingsMixin],
  data() {
    return {
      answerIds: [],
      // Bumped to rebuild the answer components after the debug panel fills in an answer
      fillCount: 0,
      rawAnswerData: {},
      servedAt: Date.now(),
      showCalculator: false,
      showResponseRequired: false,
      submitting: false,
      timeLeftInSeconds: null,
      timerInterval: null,
    };
  },
  computed: {
    session() {
      return this.$store.getters['adaptiveSim/session'];
    },
    debug() {
      return this.$store.getters['adaptiveSim/debug'];
    },
    flashcardId() {
      return this.$store.getters['adaptiveSim/flashcardId'];
    },
    flashcard() {
      return this.$store.getters['flashcards/flashcards'][this.flashcardId] || null;
    },
    // The unsent answer, shaped like a flashcard state; answer components read their selections from it
    answerState() {
      const state = { answerIds: this.answerIds, rawAnswerData: this.rawAnswerData };
      return this.isCurrentAnswered ? { ...state, flashcardId: this.flashcard?.id } : state;
    },
    usesRawAnswerData() {
      return flashcardTypesWithRawAnswerData.includes(this.flashcard?.type);
    },
    isCurrentAnswered() {
      if (this.usesRawAnswerData) {
        if (this.flashcard?.type === 'Matrix Grid') {
          const rows = this.rawAnswerData?.matrixGridSelections;
          return Array.isArray(rows) && rows.some(r => r && Array.isArray(r.selections) && r.selections.some(Boolean));
        }
        return !_.isEmpty(this.rawAnswerData);
      }
      return this.answerIds.length > 0;
    },
    userName() {
      const user = this.$store.getters['user/get'];
      return `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    },
  },
  watch: {
    flashcardId: {
      immediate: true,
      handler() {
        this.answerIds = [];
        this.rawAnswerData = {};
        this.servedAt = Date.now();
        this.showResponseRequired = false;
        this.loadTopics();
      },
    },
  },
  mounted() {
    this.timerInterval = setInterval(this.tick, 250);
  },
  beforeUnmount() {
    clearInterval(this.timerInterval);
  },
  methods: {
    answersSelected(answerIds) {
      this.answerIds = Array.isArray(answerIds) ? [...answerIds] : [];
    },
    // Debug panel: puts the correct answer on the question
    fillCorrect(response) {
      if (response?.rawAnswerData) {
        this.rawAnswerData = _.cloneDeep(response.rawAnswerData);
        this.answerIds = [];
      } else {
        this.answerIds = [...(response?.answerIds || [])];
        this.rawAnswerData = {};
      }
      this.fillCount += 1;
    },
    setRawAnswerData(rawAnswerData) {
      this.rawAnswerData = rawAnswerData || {};
    },
    // Counts down to the server's expiry and finishes the attempt when time is up
    tick() {
      if (!this.session?.expiresAt) return;
      const now = Date.now() + this.$store.getters['adaptiveSim/clockOffset'];
      this.timeLeftInSeconds = (Date.parse(this.session.expiresAt) - now) / 1000;
      if (this.timeLeftInSeconds <= 0) {
        clearInterval(this.timerInterval);
        this.finish();
      }
    },
    finish() {
      return this.$store.dispatch('adaptiveSim/finish', this.session.id).then(() => this.goToResult());
    },
    submitAnswer() {
      if (this.submitting || !this.flashcard) return;
      if (!this.isCurrentAnswered) {
        this.showResponseRequired = true;
        return;
      }

      this.submitting = true;
      const params = {
        sessionId: this.session.id,
        flashcardId: this.flashcard.id,
        position: this.session.position,
        timeInSeconds: Math.round((Date.now() - this.servedAt) / 1000),
      };
      if (this.usesRawAnswerData) {
        params.rawAnswerData = this.rawAnswerData;
      } else {
        params.answerIds = this.answerIds;
      }

      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.simulation,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
      });

      return this.$store.dispatch('adaptiveSim/answer', params)
        .then(() => {
          if (this.session.status !== 'in_progress') this.goToResult();
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.submitting = false;
        });
    },
    goToResult() {
      this.$analytics.events.simulationFinish({
        simulationId: this.simId,
        simulationName: this.simulation?.name,
      });
      return this.$router.push({
        name: 'adaptiveSim#result',
        params: { simId: this.simId, sessionId: this.session.id },
      });
    },
    // Patient box and exhibit topics for the current question
    loadTopics() {
      if (!this.flashcard) return;
      const ids = [
        this.getScenarioBoxTopicId(this.flashcard.id),
        +this.getAssetTrayGrouping(this.flashcard)?.assetAssociations?.[0]?.assetBContext,
      ].filter(id => id);
      if (ids.length) this.$store.dispatch('topics/load', { ids });
    },
  },
};
</script>
<style scoped lang="scss">
.flashcard-content {
  display: grid;
  grid-template-columns: 1fr;
  max-width: 50rem;
  min-height: 100%;
  margin: 0 auto;

  &.two-column:has(.scenario-box, .asset-tray) {
    max-width: 100%;
    grid-template-columns: 1fr 1fr;
  }

  // second child has a border on the left
  > :nth-child(1) {
    border-right: 1px solid #d9d9d9;
  }

  > * {
    padding: 1.5rem;
  }

  .first-column {
    display: none;
    min-width: 40%;

    &:has(.scenario-box, .asset-tray) {
      display: block;
    }
  }
}

.first-column > :first-child {
  margin-top: 0;
}
</style>
